import { useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from '../App';
import { isRuntimeDevMode } from '../config/devFlags';
import { isDemoMode } from '../config/demoMode';
import { setSessionToken } from '../api/client';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = sessionStorage.getItem('baba_session_token') ?? '';
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });
}

// Interview flow pages where a demo session started via /demo can skip.
const DEMO_SKIP_PATHS = [
  '/interview-prep',
  '/behavioral-interview',
  '/interview-complete',
  '/case-sim-prep',
  '/case-simulation',
];

/**
 * Cmd+Option+Shift+S — skip the current step.
 *
 * Only active when dev mode is on (toggle with Cmd+Option+Shift+D first,
 * or when VITE_ALLOW_DEV_SHORTCUT=true).
 *
 * Dev mode shows a toast so you know what happened. Demo sessions get an
 * on-screen Skip button instead and show no debug toasts.
 */
export function useDevSkip(): { showDemoSkip: boolean; skip: () => void } {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useContext(AppContext);
  const skipping = useRef(false);

  const showToast = useCallback((msg: string, failed = false) => {
    if (isDemoMode() && !isRuntimeDevMode()) {
      if (!failed) return;
      msg = 'This step could not be skipped. Try again.';
    }
    const el = document.createElement('div');
    el.textContent = msg;
    Object.assign(el.style, {
      position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
      background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: '8px',
      fontSize: '13px', fontFamily: 'monospace', zIndex: '99999', opacity: '0',
      transition: 'opacity 0.3s',
    });
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 300); }, 2500);
  }, []);

  const handleSkip = useCallback(async () => {
    if (skipping.current) return;
    skipping.current = true;
    const path = location.pathname;

    try {
      // ── Pre-interview pages: just set state + navigate ──

      if (path === '/' || path === '/meet-connor') {
        showToast('DEV SKIP → Create Profile');
        navigate('/create-profile');
        return;
      }

      if (path === '/create-profile') {
        showToast('DEV SKIP → Creating fresh candidate…');
        const uid = crypto.randomUUID().slice(0, 8);
        const resp = await apiFetch('/api/candidates', {
          method: 'POST',
          body: JSON.stringify({
            name: `Dev ${uid}`, email: `dev-${uid}@test.com`,
            phone: '+15555550100', location: 'Dev City', languages: ['english'],
          }),
        });
        if (!resp.ok) {
          const text = await resp.text();
          showToast(`DEV SKIP failed: ${resp.status} — ${text.slice(0, 100)}`, true);
          return;
        }
        const candidate = await resp.json();
        setSessionToken(candidate.session_token, candidate.id);
        dispatch({ type: 'RESET' });
        dispatch({ type: 'SET_CANDIDATE', payload: candidate });
        showToast(`DEV SKIP → New candidate ${uid}`);
        navigate('/identity-verify');
        return;
      }

      if (path === '/identity-verify') {
        dispatch({ type: 'SET_IDENTITY_STATUS', payload: 'approved' });
        showToast('DEV SKIP → Identity approved → Interview Prep');
        navigate('/interview-prep');
        return;
      }

      if (path === '/fetching-resume' || path === '/upload-resume' || path === '/resume-review') {
        dispatch({
          type: 'SET_PARSED_RESUME',
          payload: { name: 'Dev Tester', experience: [{ title: 'Care Coordinator', employer: 'Test Health', responsibilities: [] }], certifications: [], licenses: [], clinical_settings: [], education: [], languages: [], skills: [] },
        });
        dispatch({ type: 'SET_SCREENING', payload: { passed: true, reason: null } });
        showToast('DEV SKIP → Interview Prep');
        navigate('/interview-prep');
        return;
      }

      // ── Stage 1: Use dev-skip endpoint (creates + transcripts + scores in one call) ──

      if (path === '/interview-prep' || path === '/behavioral-interview') {
        const candidateId = state.candidate?.id;
        if (!candidateId) { showToast('DEV SKIP: No candidate — go to /create-profile first', true); return; }

        showToast('DEV SKIP → Skipping Stage 1 with passing transcript…');

        const resp = await apiFetch('/api/interviews/dev-skip', {
          method: 'POST',
          body: JSON.stringify({ candidate_id: candidateId, stage: 1, consent_given: true }),
        });
        if (!resp.ok) {
          const text = await resp.text();
          showToast(`DEV SKIP failed: ${resp.status} — ${text.slice(0, 100)}`, true);
          return;
        }
        const iv = await resp.json();
        dispatch({ type: 'SET_INTERVIEW_SESSION', payload: iv });

        showToast('DEV SKIP → Scoring Stage 1… (will auto-pass)');
        navigate('/interview-complete');
        return;
      }

      // ── InterviewComplete (scoring wait): inject passing score + advance ──

      if (path === '/interview-complete') {
        const candidateId = state.candidate?.id;
        if (!candidateId) { showToast('DEV SKIP: No candidate', true); return; }

        showToast('DEV SKIP → Checking for Stage 1 score…');

        // Poll briefly for real score (scoring may already be running)
        let score = null;
        for (let i = 0; i < 15; i++) {
          const resp = await apiFetch(`/api/candidates/${candidateId}/readiness`);
          const scores = await resp.json();
          score = scores.find((s: { stage: number }) => s.stage === 1);
          if (score) break;
          await new Promise(r => setTimeout(r, 2000));
        }

        if (score) {
          dispatch({ type: 'SET_STAGE1_SCORE', payload: score });
          dispatch({ type: 'SET_OVERALL_STATUS', payload: score.passed_threshold ? 'stage1_passed' : 'stage1_failed' });
        } else {
          // Force pass even if scoring hasn't finished
          dispatch({ type: 'SET_OVERALL_STATUS', payload: 'stage1_passed' });
        }

        showToast('DEV SKIP → Case Sim Prep');
        navigate('/case-sim-prep');
        return;
      }

      // ── Stage 2: Step through phases in the case sim UI ──

      if (path === '/case-simulation') {
        // Dispatch custom event — CaseSimRoom listens and advances one phase at a time
        window.dispatchEvent(new Event('dev-advance-phase'));
        showToast('DEV SKIP → Next phase');
        return;
      }

      if (path === '/case-sim-prep') {
        const candidateId = state.candidate?.id;
        if (!candidateId) { showToast('DEV SKIP: No candidate', true); return; }

        showToast('DEV SKIP → Skipping Stage 2 with passing transcript…');

        const resp = await apiFetch('/api/interviews/dev-skip', {
          method: 'POST',
          body: JSON.stringify({ candidate_id: candidateId, stage: 2, consent_given: true }),
        });
        if (!resp.ok) {
          const text = await resp.text();
          showToast(`DEV SKIP failed: ${resp.status} — ${text.slice(0, 100)}`, true);
          return;
        }
        const iv = await resp.json();
        dispatch({ type: 'SET_INTERVIEW_SESSION', payload: iv });
        dispatch({ type: 'SET_OVERALL_STATUS', payload: 'stage2_complete' });

        showToast('DEV SKIP → Results (scoring in background)');
        navigate('/results');
        return;
      }

      if (path === '/results') {
        showToast('DEV SKIP: Already at final page');
        return;
      }

      showToast(`DEV SKIP: No action for ${path}`);
    } catch (err) {
      showToast(`DEV SKIP error: ${err instanceof Error ? err.message : String(err)}`, true);
      console.error('[Dev Skip]', err);
    } finally {
      skipping.current = false;
    }
  }, [location.pathname, state.candidate, state.interviewSession, dispatch, navigate, showToast]);

  const showDemoSkip = isDemoMode() && DEMO_SKIP_PATHS.includes(location.pathname);

  useEffect(() => {
    // Skip shortcut works when runtime dev mode is on (toggled via
    // Cmd+Option+Shift+D), or — for the investor demo only — on the interview
    // flow pages of a demo-mode session started via /demo. The backend
    // dev-skip endpoint independently authorizes demo candidates, so this
    // works on prod without any dev env flags. No other route gets the
    // shortcut in production.
    if (!isRuntimeDevMode() && !showDemoSkip) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey && e.shiftKey && e.altKey && e.code === 'KeyS') {
        e.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleSkip, showDemoSkip]);

  return { showDemoSkip, skip: () => void handleSkip() };
}
